import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { MessageCard } from "./MessageCard";
import { MakeCallModal } from "./MakeCallModal";
import { SuggestedReplyCard } from "./SuggestedReplyCard";
import {
  Message,
  type ConversationFlagClear,
  type ReplyTarget
} from "../types";
import {
  useRetryEmail,
  useDeleteMessage,
  useSendSms,
  useSendEmail,
  useConversation,
  useDismissSuggestion,
  useRetrySuggestion,
  useResolveSuggestion
} from "../api/hooks";
import { useAuthContext, useVoiceEnabled } from "providers/Auth";
import { useCrmContact } from "../api/crm";
import { useOrgUsers } from "../api/org-users";
import {
  useAllPhoneNumbers,
  usePropertyPhoneDefaults
} from "../../phone-numbers/lib/hooks";
import { isReplyableEmail } from "../utils/replyable";
import { plainTextToHtml } from "../utils/plainTextToHtml";
import { useInboxEmbed } from "../context/useInboxEmbed";
import { escalationLabel, type ConversationEscalation } from "../escalation";
import { FlagClearLine } from "./FlagClearLine";
import { placeFlagClears } from "../utils/flagClears";
import { cx } from "shared/utils/classnames";
import { highlightWords } from "../utils/highlightWords";

// The pop-up's date separators are relative (design 2152:10192); anything
// older than yesterday falls back to the long-form date /inbox always shows.
const relativeDayLabel = (isoDate: string, fallback: string): string => {
  const dayDiff = DateTime.now()
    .startOf("day")
    .diff(DateTime.fromISO(isoDate).startOf("day"), "days").days;
  if (dayDiff === 0) return "Today";
  if (dayDiff === 1) return "Yesterday";
  return fallback;
};

const callTimestamp = (sentAt: DateTime): string => {
  if (!sentAt.isValid) return "";
  const day = relativeDayLabel(
    sentAt.toISO() ?? "",
    sentAt.toFormat("MMM d, yyyy")
  );
  return `${day} at ${sentAt.toFormat("h:mm a")}`;
};

const normalizePhone = (phone: string | null | undefined): string =>
  (phone ?? "").replace(/\D/g, "");

const NewMessagesDivider = ({ visible }: { visible: boolean }) => (
  <div
    className="grid transition-all duration-300 ease-in-out"
    style={{
      gridTemplateRows: visible ? "1fr" : "0fr",
      opacity: visible ? 1 : 0,
      margin: visible ? undefined : 0
    }}
  >
    <div className="overflow-hidden">
      <div className="flex items-center my-4">
        <div className="flex-1 border-t border-primary-500" />
        <span className="text-sm font-bold text-white bg-primary-500 px-2 py-1 rounded-md">
          New
        </span>
        <div className="flex-1 border-t border-primary-500" />
      </div>
    </div>
  </div>
);

interface PartyFieldContext {
  isInbound: boolean;
  contactName: string;
  senderName: string;
  resolveUserPhoneName: (phone: string) => string;
}

// "To" line of a message card: the recipient side per channel. Inbound
// SMS/voice land on the partner's own number, so the phone-number lookup (not
// the contact) labels it (CRM-905 for voice).
const buildToField = (
  message: Message,
  { isInbound, contactName, resolveUserPhoneName }: PartyFieldContext
): { name: string; address: string } | undefined => {
  // Outbound mail goes to the contact; inbound lands in one of our mailboxes,
  // which has no display name here — leave it blank rather than repeat the
  // address as the name (the pop-up card showed "addr • addr").
  if (message.channel === "email")
    return {
      name: isInbound ? "" : contactName,
      address: message.email_details?.to_addresses[0] || ""
    };
  if (message.channel === "sms") {
    const toNumber = message.sms_details?.to_number || "";
    const name = isInbound ? resolveUserPhoneName(toNumber) : contactName;
    return { name, address: toNumber };
  }
  if (message.channel === "voice") {
    const toNumber = message.voice_details?.to_number || "";
    const name = isInbound
      ? message.voice_details?.to_name || resolveUserPhoneName(toNumber)
      : message.voice_details?.to_name || contactName;
    return { name, address: toNumber };
  }
  if (message.channel === "note") return { name: contactName, address: "" };
  return undefined;
};

// "From" line: the sender side per channel. Inbound voice "from" is the
// caller — fall back to the conversation's contact name. Outbound: the
// projection's label wins; the phone-number lookup covers calls placed before
// from_name was captured (CRM-905).
const buildFromField = (
  message: Message,
  {
    isInbound,
    contactName,
    senderName,
    resolveUserPhoneName
  }: PartyFieldContext
): { name: string; address: string } | undefined => {
  if (message.channel === "email") {
    const addr = message.email_details?.from_address || senderName;
    return { name: senderName, address: addr };
  }
  if (message.channel === "sms") {
    const fromNumber = message.sms_details?.from_number || "";
    const name = isInbound ? contactName : resolveUserPhoneName(fromNumber);
    return { name, address: fromNumber };
  }
  if (message.channel === "voice") {
    const fromNumber = message.voice_details?.from_number || "";
    const name = isInbound
      ? message.voice_details?.from_name || contactName
      : message.voice_details?.from_name || resolveUserPhoneName(fromNumber);
    return { name, address: fromNumber };
  }
  return undefined;
};

// Who a message row is from, presentation-wise. Call transcripts are
// contact-authored notes. For messages sent by a user, prefer the user's name
// over the raw phone/email identifier so shared inboxes show who actually
// sent it. Automated messages have no real user: workflow SMS use sender_type
// "system", while workflow email/note use the sentinel sender_id 0 — both are
// labelled "System".
const deriveSender = (
  message: Message,
  contactName: string,
  userNameById: Map<string, string>
): { isFromContact: boolean; isInbound: boolean; senderName: string } => {
  const isCallTranscript =
    message.channel === "note" &&
    message.note_details?.type === "call_transcript";
  const isFromContact = message.sender_type === "contact" || isCallTranscript;
  const isInbound = message.direction === "inbound" || isCallTranscript;
  const isAutomated =
    message.sender_type === "system" || message.sender_id === 0;
  const senderUserName =
    message.sender_type === "user" && message.sender_id
      ? userNameById.get(String(message.sender_id))
      : undefined;
  const outboundSenderName = isAutomated
    ? "System"
    : senderUserName || message.sender_identifier || "You";
  return {
    isFromContact,
    isInbound,
    senderName: isInbound ? contactName : outboundSenderName
  };
};

// communication-service stores every note as outbound, so a call transcript's
// `direction` says nothing about the call. An AI call only reaches the inbox
// when the lead called a Boom number (ElevenLabs::PushToInbox), so its card is
// always inbound and stays white like any other inbound call (CRM-1712).
const getCardIsInbound = (message: Message): boolean | undefined => {
  if (message.note_details?.type === "call_transcript") return true;
  if (message.channel === "voice")
    return message.direction ? message.direction === "inbound" : undefined;
  return undefined;
};

interface MessagesListProps {
  messages: Message[];
  conversationId?: string;
  contactId?: string;
  contactName: string;
  /** Selected SMS "from" number (communication_service_id) from the composer. */
  smsFromPhoneId?: string;
  highlightMessageId?: string;
  highlightQuery?: string;
  /** Labels and outlines the inbound message that escalated the conversation. */
  escalation?: ConversationEscalation | null;
  flagClears?: ConversationFlagClear[];
  unreadCount?: number;
  onLoadMoreBefore?: () => void;
  onLoadMoreAfter?: () => void;
  hasMoreBefore?: boolean;
  hasMoreAfter?: boolean;
  isLoadingBefore?: boolean;
  isLoadingAfter?: boolean;
  onReply?: (target: ReplyTarget) => void;
}

export const MessagesList = ({
  messages,
  conversationId,
  contactId,
  contactName,
  smsFromPhoneId,
  highlightMessageId,
  highlightQuery,
  escalation,
  flagClears,
  unreadCount = 0,
  onLoadMoreBefore,
  onLoadMoreAfter,
  hasMoreBefore,
  hasMoreAfter,
  isLoadingBefore,
  isLoadingAfter,
  onReply
}: MessagesListProps) => {
  const retryEmail = useRetryEmail();
  const deleteMessage = useDeleteMessage();
  const sendSms = useSendSms();
  const sendEmail = useSendEmail();
  const dismissSuggestion = useDismissSuggestion();
  const retrySuggestion = useRetrySuggestion();
  const resolveSuggestion = useResolveSuggestion();
  const { type } = useAuthContext();
  // "Call back" places an outbound call, so it needs the outbound permission — not
  // merely a registered Device, which an inbound-only user now also has (CRM-1169).
  const isVoiceEnabled = useVoiceEnabled();
  const { data: conversationData } = useConversation(conversationId || "");
  // Show suggestions only when the partner feature flag is on AND the
  // per-conversation toggle is on (and no stale `ready` row when off).
  const aiSuggestionsEnabled =
    type.isCrmAiVoiceAgent &&
    (conversationData?.conversation?.ai_suggestions_enabled ?? true);

  // Everything that is not SMS replies by email, so the card is only offered
  // when the partner actually has that channel (CRM-1132).
  const suggestionChannelAvailable = (suggestion: { channel?: string }) =>
    suggestion.channel === "sms" || type.isCommunicationEmail;

  // AI-216: session-local instant hide; persistence happens via the
  // useDismissSuggestion mutation in handleDismissSuggestion.
  const [dismissedSuggestionIds, setDismissedSuggestionIds] = useState<
    Set<string>
  >(new Set());
  const [sendingSuggestionId, setSendingSuggestionId] = useState<string | null>(
    null
  );
  // Outbound messages sent from a suggestion this session — the server only
  // links message → suggestion after the send resolves, so the optimistically
  // appended message doesn't carry `from_ai_suggestion` until a refetch.
  const [aiSentMessageIds, setAiSentMessageIds] = useState<Set<string>>(
    new Set()
  );

  // Reopened from a failed outbound call's "Try again". Carries the failed
  // call's original destination number so the redial targets the SAME number
  // instead of falling back to the contact's first phone. The lead activity
  // log is attached server-side by phone match, so no lead id is needed here.
  const [retryCall, setRetryCall] = useState<{
    toNumber: string;
  } | null>(null);

  // SMS and voice both label a partner-owned number, so both need the phone
  // lookups that turn a number into its property / friendly name.
  const hasPhoneMessages = messages.some(
    (m) => m.channel === "sms" || m.channel === "voice"
  );
  const hasUserSentMessages = messages.some((m) => m.sender_type === "user");
  const { data: crmContact } = useCrmContact(
    hasPhoneMessages && contactId ? contactId : ""
  );
  const { data: orgUsers } = useOrgUsers({ enabled: hasUserSentMessages });
  const { data: phoneNumbersData } = useAllPhoneNumbers({
    enabled: hasPhoneMessages
  });
  const { data: propertyDefaults } = usePropertyPhoneDefaults(hasPhoneMessages);

  // Default SMS "from" number — mirrors SmsRoutingSelect's effectiveFrom
  // fallback (first active can_sms number) so an SMS suggestion can send even
  // while the composer sits on the email/manual tab and no number was picked.
  const defaultSmsFromPhoneId = useMemo(() => {
    const active =
      phoneNumbersData?.items.filter(
        (phoneNumber) => phoneNumber.status === "active" && phoneNumber.can_sms
      ) ?? [];
    const sorted = [...active].sort((first, second) =>
      (first.phone_number ?? "").localeCompare(second.phone_number ?? "")
    );
    return sorted[0]?.communication_service_id ?? "";
  }, [phoneNumbersData]);

  // The composer's explicit selection wins over the default.
  const effectiveSmsFromPhoneId = smsFromPhoneId || defaultSmsFromPhoneId;

  const propertyNameByPhone = useMemo(() => {
    const result = new Map<string, string>();
    if (!propertyDefaults || !crmContact?.leads) return result;

    const propertyIdToName = new Map<number, string>();
    for (const lead of crmContact.leads)
      if (lead.property?.name)
        propertyIdToName.set(lead.property_id, lead.property.name);

    for (const def of propertyDefaults) {
      const name = propertyIdToName.get(def.property_id);
      if (name) result.set(normalizePhone(def.phone_number), name);
    }
    return result;
  }, [propertyDefaults, crmContact]);

  const friendlyNameByPhone = useMemo(() => {
    const result = new Map<string, string>();
    if (!phoneNumbersData?.items) return result;
    for (const pn of phoneNumbersData.items)
      if (pn.phone_number && pn.name)
        result.set(normalizePhone(pn.phone_number), pn.name);
    return result;
  }, [phoneNumbersData]);

  const resolveUserPhoneName = useCallback(
    (phone: string): string => {
      const key = normalizePhone(phone);
      return propertyNameByPhone.get(key) ?? friendlyNameByPhone.get(key) ?? "";
    },
    [propertyNameByPhone, friendlyNameByPhone]
  );

  const userNameById = useMemo(() => {
    const result = new Map<string, string>();
    if (!orgUsers) return result;
    for (const user of orgUsers)
      if (user.name) result.set(String(user.id), user.name);
    return result;
  }, [orgUsers]);

  const handleDismissSuggestion = useCallback(
    (messageId: string) => {
      // Hide instantly for this session…
      setDismissedSuggestionIds((prev) => new Set(prev).add(messageId));
      // …and persist so it stays dismissed across reloads.
      dismissSuggestion.mutate(messageId);
    },
    [dismissSuggestion]
  );

  // Retry a failed suggestion — backend resets it to generating + regenerates,
  // then broadcasts suggestion_pending/ready which refetches the thread.
  const handleRetrySuggestion = useCallback(
    (messageId: string) => {
      retrySuggestion.mutate(messageId);
    },
    [retrySuggestion]
  );

  // Send the AI draft as-is. SMS goes out from the number selected in the
  // composer's "From" dropdown, falling back to the default active number;
  // email replies inherit subject/recipient server-side from the anchor
  // message. On success the outbound supersedes the suggestion server-side,
  // so it clears on refetch.
  const handleSendSuggestion = useCallback(
    (message: Message, text: string) => {
      const suggestion = message.suggested_reply;
      const finalText = text.trim();
      if (!conversationId || !suggestion || !finalText) return;
      // SMS requires a "from" number (Send is disabled otherwise).
      if (suggestion.channel === "sms" && !effectiveSmsFromPhoneId) return;
      // Anything not SMS replies by email below, which needs the email channel.
      if (suggestion.channel !== "sms" && !type.isCommunicationEmail) return;

      setSendingSuggestionId(message.id);
      const onDone = () => setSendingSuggestionId(null);

      // Edit-capture: record what was actually sent (edited vs accepted-as-is).
      // Best-effort — never block the send/UI on this.
      const recordResolution = (sentMessage: { id?: string } | undefined) => {
        if (sentMessage?.id) {
          const sentId = sentMessage.id;
          setAiSentMessageIds((prev) => new Set(prev).add(sentId));
        }
        resolveSuggestion.mutate({
          anchor_message_id: message.id,
          final_sent_text: finalText,
          ...(sentMessage?.id ? { outbound_message_id: sentMessage.id } : {})
        });
      };

      if (suggestion.channel === "sms") {
        const toNumber = message.sms_details?.from_number;
        if (!toNumber) {
          toast.error("No phone number to reply to on this message");
          onDone();
          return;
        }
        sendSms.mutate(
          {
            conversation_id: conversationId,
            to_number: toNumber,
            content: finalText,
            phone_number_id: effectiveSmsFromPhoneId
          },
          {
            onSuccess: (data) => recordResolution(data?.message),
            onError: () => toast.error("Failed to send SMS"),
            onSettled: onDone
          }
        );
        return;
      }

      // email — reply-all on the anchor thread; the server inherits the
      // subject and derives To/Cc, matching the card's reply_recipients line.
      sendEmail.mutate(
        {
          conversation_id: conversationId,
          reply_to_message_id: message.id,
          reply_all: true,
          content_plain: finalText,
          content_html: plainTextToHtml(finalText),
          ...(contactId ? { contact_id: Number(contactId) } : {})
        },
        {
          onSuccess: (data) => recordResolution(data?.message),
          onError: () => toast.error("Failed to send email"),
          onSettled: onDone
        }
      );
    },
    [
      conversationId,
      contactId,
      effectiveSmsFromPhoneId,
      sendSms,
      sendEmail,
      resolveSuggestion
    ]
  );

  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const hasScrolledToHighlight = useRef<string | null>(null);
  const [observersReady, setObserversReady] = useState(!highlightMessageId);
  const clearWordHighlight = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (highlightQuery) return;
    clearWordHighlight.current?.();
    clearWordHighlight.current = null;
  }, [highlightQuery]);

  useEffect(() => () => clearWordHighlight.current?.(), []);

  const handleHighlightScroll = useCallback(
    (el: HTMLDivElement, id: string) => {
      hasScrolledToHighlight.current = id;
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("highlight-message");
        setTimeout(() => el.classList.remove("highlight-message"), 2000);
        if (highlightQuery) {
          clearWordHighlight.current?.();
          clearWordHighlight.current = highlightWords(el, highlightQuery);
        }
        setObserversReady(true);
      }, 300);
    },
    [highlightQuery]
  );

  const setMessageRef = useCallback(
    (id: string) => (el: HTMLDivElement | null) => {
      if (el) {
        messageRefs.current.set(id, el);

        if (id === highlightMessageId && hasScrolledToHighlight.current !== id)
          handleHighlightScroll(el, id);
      } else messageRefs.current.delete(id);
    },
    [highlightMessageId, handleHighlightScroll]
  );

  useEffect(() => {
    if (
      !observersReady ||
      !hasMoreBefore ||
      isLoadingBefore ||
      !topSentinelRef.current
    )
      return;

    let skipInitial = true;
    // eslint-disable-next-line compat/compat -- IntersectionObserver is supported in all target browsers
    const observer = new IntersectionObserver(
      (entries) => {
        if (skipInitial) {
          skipInitial = false;
          return;
        }
        if (entries[0].isIntersecting) onLoadMoreBefore?.();
      },
      { threshold: 0.1 }
    );

    observer.observe(topSentinelRef.current);
    return () => observer.disconnect();
  }, [observersReady, hasMoreBefore, isLoadingBefore, onLoadMoreBefore]);

  useEffect(() => {
    if (
      !observersReady ||
      !hasMoreAfter ||
      isLoadingAfter ||
      !bottomSentinelRef.current
    )
      return;

    let skipInitial = true;
    // eslint-disable-next-line compat/compat -- IntersectionObserver is supported in all target browsers
    const observer = new IntersectionObserver(
      (entries) => {
        if (skipInitial) {
          skipInitial = false;
          return;
        }
        if (entries[0].isIntersecting) onLoadMoreAfter?.();
      },
      { threshold: 0.1 }
    );

    observer.observe(bottomSentinelRef.current);
    return () => observer.disconnect();
  }, [observersReady, hasMoreAfter, isLoadingAfter, onLoadMoreAfter]);

  const dividerIndexRef = useRef(-1);
  const isCompact = Boolean(useInboxEmbed()?.compact);
  const showDivider = unreadCount > 0 && unreadCount < messages.length;

  if (showDivider) dividerIndexRef.current = messages.length - unreadCount;

  const newDividerIndex = dividerIndexRef.current;

  const placedFlagClears = useMemo(
    () => placeFlagClears(messages, flagClears),
    [messages, flagClears]
  );

  return (
    <div className="mx-auto max-w-[1000px] space-y-6">
      {observersReady && hasMoreBefore && (
        <div ref={topSentinelRef} className="h-4">
          {isLoadingBefore && (
            <div className="text-center text-xs text-content-400">
              Loading older messages...
            </div>
          )}
        </div>
      )}

      {hasMoreBefore
        ? null
        : placedFlagClears.before.map((clear) => (
            <FlagClearLine key={clear.id} clear={clear} compact={isCompact} />
          ))}

      {messages.map((message, index) => {
        const showDateSeparator =
          index === 0 ||
          !messages[index - 1] ||
          new Date(message.created_at).toDateString() !==
            new Date(messages[index - 1].created_at).toDateString();

        const formattedDate = new Date(message.created_at).toLocaleDateString(
          "en-US",
          {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric"
          }
        );

        const separatorLabel = isCompact
          ? relativeDayLabel(message.created_at, formattedDate)
          : formattedDate;

        const dateStr = message.sent_at || message.created_at;
        const sentAt = DateTime.fromISO(dateStr);
        const timestamp =
          message.note_details?.type === "call_transcript"
            ? callTimestamp(sentAt)
            : sentAt.toRelative() || "";

        const { isFromContact, isInbound, senderName } = deriveSender(
          message,
          contactName,
          userNameById
        );

        const partyFieldContext = {
          isInbound,
          contactName,
          senderName,
          resolveUserPhoneName
        };
        const toField = buildToField(message, partyFieldContext);
        const fromField = buildFromField(message, partyFieldContext);

        // Voice redial calls the external party back: the callee of an
        // outbound call, or the CALLER of an inbound one (CRM-905).
        const voiceCallBackNumber = isInbound
          ? message.voice_details?.from_number
          : message.voice_details?.to_number;

        const cardIsInbound = getCardIsInbound(message);

        return (
          <div
            key={message.id}
            ref={setMessageRef(message.id)}
            data-message-id={message.id}
            className="transition-colors duration-500"
          >
            {showDateSeparator && (
              <div
                className={cx(
                  "text-center font-medium text-content-300",
                  isCompact ? "mb-2 text-sm" : "mb-6 text-xs"
                )}
              >
                {separatorLabel}
              </div>
            )}

            {index === newDividerIndex && (
              <NewMessagesDivider visible={showDivider} />
            )}

            <MessageCard
              channel={message.channel}
              messageId={message.id}
              onReply={
                onReply && isReplyableEmail(message) ? onReply : undefined
              }
              from={fromField}
              to={toField}
              toAddresses={message.email_details?.to_addresses}
              ccAddresses={message.email_details?.cc_addresses}
              bccAddresses={message.email_details?.bcc_addresses}
              subject={message.email_details?.subject}
              isReply={!!message.email_details?.in_reply_to}
              sentViaQuotaFallback={
                !!message.email_details?.sent_via_quota_fallback
              }
              openedAt={message.email_details?.opened_at}
              body={message.content_plain || ""}
              htmlBody={
                message.channel === "note"
                  ? (message.note_details?.content_html ?? undefined)
                  : (message.email_details?.content_html ?? undefined)
              }
              timestamp={timestamp}
              sentBy={senderName || ""}
              avatar={{
                initials: (senderName || "?").substring(0, 2).toUpperCase(),
                color: isFromContact ? "#6366F1" : "#E6007E"
              }}
              isAiGenerated={
                !isInbound &&
                (message.from_ai_suggestion || aiSentMessageIds.has(message.id))
              }
              autoSent={!isInbound && !!message.ai_auto_sent}
              isFromContact={isFromContact}
              noteType={message.note_details?.type}
              noteDetails={message.note_details}
              voiceDetails={message.voice_details}
              attachments={message.attachments}
              escalationLabel={
                escalation?.messageId === message.id
                  ? escalationLabel(escalation.reason)
                  : undefined
              }
              sentAsSms={message.chat_details?.sent_as_sms}
              sentAsSmsTo={message.chat_details?.sent_as_sms_to}
              sentAsSmsFrom={message.chat_details?.sent_as_sms_from}
              receivedViaSms={message.chat_details?.received_via_sms}
              // SMS mirror only applies to BoomCRM partners (they have phone
              // numbers); BoomScreen-only partners never mirror, so don't warn.
              smsFailedReason={
                type.isBoomCRM
                  ? message.chat_details?.sent_as_sms_failed_reason
                  : undefined
              }
              status={message.status}
              smsKeywordType={message.sms_keyword_type}
              failureReason={message.failure_reason}
              retryAllowed={message.retry_allowed}
              retryDisabledReason={message.retry_disabled_reason}
              autoRetryAt={message.auto_retry_at}
              onRetry={() => retryEmail.mutate(message.id)}
              onDelete={() => deleteMessage.mutate(message.id)}
              isInbound={cardIsInbound}
              onVoiceRetry={
                voiceCallBackNumber && isVoiceEnabled
                  ? () => setRetryCall({ toNumber: voiceCallBackNumber })
                  : undefined
              }
              isRetrying={
                retryEmail.isPending && retryEmail.variables === message.id
              }
            />

            {aiSuggestionsEnabled &&
              message.suggested_reply &&
              suggestionChannelAvailable(message.suggested_reply) &&
              !dismissedSuggestionIds.has(message.id) && (
                <div className="mt-4">
                  <SuggestedReplyCard
                    suggestion={message.suggested_reply}
                    onSend={(text) => handleSendSuggestion(message, text)}
                    onDismiss={() => handleDismissSuggestion(message.id)}
                    onRetry={() => handleRetrySuggestion(message.id)}
                    isRetrying={
                      retrySuggestion.isPending &&
                      retrySuggestion.variables === message.id
                    }
                    isSending={sendingSuggestionId === message.id}
                    sendDisabled={
                      message.suggested_reply.channel === "sms" &&
                      !effectiveSmsFromPhoneId
                    }
                    sendDisabledReason="No active SMS phone number available"
                    customerPhone={
                      isInbound
                        ? message.sms_details?.from_number
                        : message.sms_details?.to_number
                    }
                  />
                </div>
              )}
            {placedFlagClears.after.get(message.id)?.map((clear) => (
              <FlagClearLine key={clear.id} clear={clear} compact={isCompact} />
            ))}
          </div>
        );
      })}

      {observersReady && hasMoreAfter && (
        <div ref={bottomSentinelRef} className="h-4">
          {isLoadingAfter && (
            <div className="text-center text-xs text-content-400">
              Loading newer messages...
            </div>
          )}
        </div>
      )}

      {retryCall && (
        <MakeCallModal
          opened
          onClose={() => setRetryCall(null)}
          contactId={contactId ?? ""}
          toPhones={[retryCall.toNumber]}
          toName={contactName}
          initialToNumber={retryCall.toNumber}
        />
      )}
    </div>
  );
};
